import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { AnalysisFileValidationIssuesDto } from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import styles from './AnalysisValidationIssuesModal.module.scss'

interface Props {
  readonly fileName: string | null
  // null keeps the modal closed.
  readonly validationIssues: AnalysisFileValidationIssuesDto | null
  readonly onClose: () => void
}

const TITLE_ID = 'analysis-validation-issues-modal-title'
const NOT_AVAILABLE = '—'

function describeProblemCount(count: number): string {
  return count === 1 ? '1 problem' : `${count} problems`
}

/**
 * Lists every problem upload validation found in an analysis file, so the
 * admin can fix the workbook without guessing from the one-line summary.
 */
export function AnalysisValidationIssuesModal({
  fileName,
  validationIssues,
  onClose,
}: Props) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const isOpen = validationIssues !== null

  useEffect(() => {
    if (!isOpen) return undefined
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    closeButtonRef.current?.focus({ preventScroll: true })
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus({ preventScroll: true })
    }
  }, [isOpen, onClose])

  if (validationIssues === null) return null

  const { totalCount, issues } = validationIssues
  const isCapped = issues.length < totalCount

  return createPortal(
    <div
      id="analysis-validation-issues-modal-backdrop"
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        id="analysis-validation-issues-modal"
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <div className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            Validation details{fileName !== null ? ` – ${fileName}` : ''}
          </h2>
          <button
            id="analysis-validation-issues-modal-close-icon-btn"
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            aria-label="Close validation details"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className={styles.body}>
          <p id="analysis-validation-issues-summary" className={styles.summary}>
            The file has {describeProblemCount(totalCount)} and cannot be used
            until they are fixed.
          </p>
          {isCapped ? (
            <p
              id="analysis-validation-issues-cap-note"
              className={styles.capNote}
            >
              Showing the first {issues.length} of {totalCount}. Fix these and
              re-upload to see the rest.
            </p>
          ) : null}
          {issues.length > 0 ? (
            <table
              id="analysis-validation-issues-table"
              className={styles.table}
            >
              <thead>
                <tr>
                  <th scope="col">Sheet</th>
                  <th scope="col">Row</th>
                  <th scope="col">Column</th>
                  <th scope="col">Problem</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue, index) => (
                  <tr
                    key={`${issue.sheet}-${issue.row}-${issue.column}-${index}`}
                  >
                    <td>{issue.sheet}</td>
                    <td className={styles.numericCell}>
                      {issue.row ?? NOT_AVAILABLE}
                    </td>
                    <td>{issue.column ?? NOT_AVAILABLE}</td>
                    <td className={styles.messageCell}>{issue.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
        <div className={styles.footer}>
          <button
            id="analysis-validation-issues-modal-close-btn"
            type="button"
            className={styles.footerButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
